import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import * as Yup from "yup";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CloudRestroUsers from "../../../services/DatabaseService/CloudRestroUsers";

// Validation schema
const validationSchema = Yup.object({
  organizationId: Yup.number()
    .required("Organization ID is required")
    .min(1, "Organization ID must be a positive number"),
  location: Yup.string().required("Location is required"),
  showHeartBeatWindow: Yup.boolean(),
});

const DeviceRegistrationForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    organizationId: "",
    macId: "",
    deviceCode: "",
    deviceName: "",
    terminal: "",
    ipAddress: "",
    location: "",
    showHeartBeatWindow: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);

  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [registrationToken, setRegistrationToken] = useState("");

  // Fetch organizations
  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoadingOrganizations(true);
      const data = await CloudRestroUsers.getWithDevices();
      setOrganizations(data || []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      toast.error("Failed to load organizations");
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const handleChange = (e) => {
    if (!e || !e.target) {
      setFormData({
        ...formData,
        organizationId: e?.value || "",
      });
      if (errors.organizationId) {
        setErrors({ ...errors, organizationId: "" });
      }
      return;
    }

    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });
  };

  const validateForm = async () => {
    try {
      const dataToValidate = {
        ...formData,
        organizationId: parseInt(formData.organizationId, 10),
      };
      await validationSchema.validate(dataToValidate, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (!isValid) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setLoading(true);
      const dataToSubmit = {
        ...formData,
        organizationId: parseInt(formData.organizationId, 10),
      };

      const response = await CloudRestroUsers.registerDevice(dataToSubmit);

      toast.success("Device registered successfully!");

      // Reset form
      setFormData({
        organizationId: "",
        macId: "",
        deviceCode: "",
        deviceName: "",
        terminal: "",
        ipAddress: "",
        location: "",
        showHeartBeatWindow: true,
      });
      setTouched({});

      // Show registration token modal
      if (response.registrationToken) {
        setRegistrationToken(response.registrationToken);
        setTokenModalOpen(true);
      }

      if (onSuccess) onSuccess(response);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to register device",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      organizationId: "",
      macId: "",
      deviceCode: "",
      deviceName: "",
      terminal: "",
      ipAddress: "",
      location: "",
      showHeartBeatWindow: true,
    });
    setErrors({});
    setTouched({});
  };

  const copyTokenToClipboard = () => {
    navigator.clipboard.writeText(registrationToken);
    toast.success("Token copied to clipboard");
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb title="Register Device" pageTitle="Devices" />
        </div>

        <Container fluid>
          <Row>
            <Col lg="10" className="mx-auto">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light">
                  <h5 className="mb-0">Device Registration</h5>
                </CardHeader>

                <CardBody className="p-4">
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label for="organizationId" className="fw-bold">
                            Organization Name{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Select
                            name="organizationId"
                            options={organizations.map((org) => ({
                              value: org.organizationId,
                              label: `${org.name} (${org.orgCode}) (${
                                org.devices?.length || 0
                              } devices)`,
                            }))}
                            value={
                              formData.organizationId
                                ? (() => {
                                    const selectedOrg = organizations.find(
                                      (org) =>
                                        org.organizationId ===
                                        parseInt(formData.organizationId),
                                    );
                                    return selectedOrg
                                      ? {
                                          value: selectedOrg.organizationId,
                                          label: `${selectedOrg.name} (${
                                            selectedOrg.orgCode
                                          }) - Devices: ${
                                            selectedOrg.devices?.length || 0
                                          }`,
                                        }
                                      : null;
                                  })()
                                : null
                            }
                            onChange={handleChange}
                            onBlur={() =>
                              setTouched({ ...touched, organizationId: true })
                            }
                            isLoading={loadingOrganizations}
                            isClearable
                            isSearchable
                            placeholder="Select an organization"
                            className={`react-select ${
                              errors.organizationId && touched.organizationId
                                ? "is-invalid"
                                : ""
                            }`}
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderColor:
                                  errors.organizationId &&
                                  touched.organizationId
                                    ? "#dc3545"
                                    : base.borderColor,
                              }),
                            }}
                          />
                          {errors.organizationId && touched.organizationId && (
                            <div className="text-danger small mt-1">
                              {errors.organizationId}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label for="macId" className="fw-bold">
                            MAC ID
                          </Label>
                          <Input
                            type="text"
                            name="macId"
                            value={formData.macId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={Boolean(errors.macId && touched.macId)}
                          />
                          {errors.macId && touched.macId && (
                            <FormFeedback>{errors.macId}</FormFeedback>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label className="fw-bold">
                            Location <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="3">
                        <FormGroup check className="mt-4">
                          <Input
                            type="checkbox"
                            name="showHeartBeatWindow"
                            checked={formData.showHeartBeatWindow}
                            onChange={handleChange}
                          />
                          <Label check className="ms-2">
                            Show Heartbeat Window
                          </Label>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col className="d-flex gap-2 justify-content-end">
                        <Button
                          color="light"
                          type="button"
                          onClick={onClose}
                          disabled={loading}
                        >
                          Cancel
                        </Button>

                        <Button
                          color="primary"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Registering...
                            </>
                          ) : (
                            "Register Device"
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Registration Token Modal */}
        <Modal
          isOpen={tokenModalOpen}
          toggle={() => setTokenModalOpen(!tokenModalOpen)}
        >
          <ModalHeader toggle={() => setTokenModalOpen(false)}>
            Device Registration Token
          </ModalHeader>
          <ModalBody>
            <p>
              Please save this token securely. You will need it for further
              device registration.
            </p>
            <Input type="text" value={registrationToken} readOnly />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={copyTokenToClipboard}>
              Copy Token
            </Button>
            <Button color="secondary" onClick={() => setTokenModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default DeviceRegistrationForm;
